import React, { useEffect } from "react";
import { useRouteMatch } from "react-router-dom";

// @material-ui/core components
import { CircularProgress, Typography, Box } from "@material-ui/core";
import { CloudUpload, Create, DoneAll, Error } from "@material-ui/icons";

// core components
import CreateCourseCard from "views/Components/CreateCourseCard";

// context and APIs
import NotificationContext from "views/Components/Context.js";
import {
  deleteModelDevelopmentProject,
  getAllModelDevelopmentProjects,
  createModelDevelopmentProjectAPI,
  updateModelDevelopmentProjectTags,
  inviteUserToModelDevelopmentProject,
  cloneModelDevelopmentProject,
} from "views/APIs/ToolboxUtility";
import AssetDashboard from "views/Components/AssetDashboard";

// styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { makeStyles } from "@material-ui/core/styles";
import { fetchAllProjectsAPI } from "views/APIs/ProjectsUtility";
import CreateModelDevelopmentProjectModal from "./CreateModelDevelopmentProjectModal";

const useStyles = makeStyles({
  ...styles,
});

export default function ModelDevelopmentDashboard() {
  // Import images from assets
  const images = require.context("assets/img/create_cards_16_9/", true);
  const path = useRouteMatch().path;
  const classes = useStyles();

  const [project_id, setProjectId] = React.useState("");

  // Context and state variables
  const { showNotification } = React.useContext(NotificationContext);

  // State variables for project creation and management
  const [createProjectModal, setCreateProjectModal] = React.useState(false);
  const [newProjectDetails, setNewProjectDetails] = React.useState({
    name: "",
    description: "",
  });
  const [
    allModelDevelopmentProjects,
    setAllModelDevelopmentProjects,
  ] = React.useState([]);
  const [
    initialModelDevelopmentProjects,
    setInitialModelDevelopmentProjects,
  ] = React.useState([]);
  const [loading, setLoading] = React.useState({ loading: false, message: "" });
  const [projectCreationLoader, setProjectCreationLoader] = React.useState(
    false
  );
  const [projectAdded, setProjectAdded] = React.useState(false);
  const [projects, setProjects] = React.useState([]);
  const [selectedProject, setSelectedProject] = React.useState("");

  // Handle project deletion
  const handleDeleteProject = async (projectData) => {
    const formData = new FormData();
    formData.append("project_id", projectData._id);
    try {
      await deleteModelDevelopmentProject(formData);
      setAllModelDevelopmentProjects(
        allModelDevelopmentProjects.filter(
          (project) => project._id !== projectData._id
        )
      );
      showNotification("Project deleted successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  useEffect(() => {
    const fetchAllProjects = async () => {
      try {
        setLoading({ loading: true, message: "Loading Projects..." });
        const data = await fetchAllProjectsAPI();
        setProjects(data);
        setSelectedProject(data?.[0]?._id || "");
      } catch (error) {
        showNotification(error, "danger");
      } finally {
        setLoading({ loading: false, message: "" });
      }
    };

    fetchAllProjects();
  }, []);

  // Handle project cloning
  const handleCloneProject = async (projectData) => {
    try {
      setLoading({ loading: true, message: "Cloning Project..." });
      const formData = new FormData();
      formData.append("project_id", projectData._id);
      const data = await cloneModelDevelopmentProject(formData);
      setAllModelDevelopmentProjects((prevProjects) => [
        ...prevProjects,
        _mapResourceToAppType(data),
      ]);
      setProjectAdded(true);
      showNotification("Project cloned successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
      setLoading({ loading: false, message: "" });
    }
  };

  useEffect(() => {
    const fetchAllModelDevelopmentProjects = async () => {
      try {
        setLoading({ loading: true, message: "Loading Projects..." });
        const data = await getAllModelDevelopmentProjects();
        const mappedData = data.map(_mapResourceToAppType);

        setAllModelDevelopmentProjects(mappedData);
        setInitialModelDevelopmentProjects(mappedData);
      } catch (error) {
        showNotification(error, "danger");
      } finally {
        setLoading({ loading: false, message: "" });
      }
    };

    fetchAllModelDevelopmentProjects();
  }, []);

  // Handle project creation
  const handleCreateProject = async () => {
    setProjectCreationLoader(true);

    try {
      // Prepare form data
      const formData = new FormData();
      formData.append("title", newProjectDetails.name);
      formData.append("description", newProjectDetails.description);

      // Create project via API call
      const data = await createModelDevelopmentProjectAPI(formData);
      window.open(`/create/model_development/model/${data._id}`, "_blank");

      setAllModelDevelopmentProjects((prevProjects) => [
        ...prevProjects,
        _mapResourceToAppType(data),
      ]);
      setProjectAdded(true);
      showNotification("Project created successfully", "success");
    } catch (error) {
      showNotification(
        `Failed to create project: ${error.message || error}`,
        "danger"
      );
    } finally {
      setProjectCreationLoader(false);
      handleCreateProjectModalClose();
    }
  };

  const handleSaveLabel = async (data) => {
    try {
      const formData = new FormData();
      formData.append("project_id", data._id);
      if (data.tags.length === 0) {
        formData.append("tags", []);
      }
      // Append each tag separately
      else {
        data.tags.forEach((tag) => formData.append("tags", tag));
      }
      await updateModelDevelopmentProjectTags(formData);
      setAllModelDevelopmentProjects((prevProjects) =>
        prevProjects.map((project) =>
          project._id === data._id ? { ...project, tags: data.tags } : project
        )
      );
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  const handleInviteSend = async (data) => {
    try {
      const formData = new FormData();
      formData.append("project_id", data._id);
      formData.append("email", data.email);

      const response = await inviteUserToModelDevelopmentProject(formData);
      const updatedProjectId = response.project_id;

      // Find the target asset
      const updatedAsset = allModelDevelopmentProjects.find(
        (asset) => asset._id === updatedProjectId
      );
      if (!updatedAsset) {
        console.warn(
          "⚠️ Could not find the matching asset for ID:",
          updatedProjectId
        );
        return;
      }

      // Create a new invitee object
      const newInvitee = {
        invitee_name: response.name,
        email: response.email,
        status: "pending",
      };
      // Update or append the invitee
      const updatedInvitees = (updatedAsset.invitees || []).map((invitee) =>
        invitee.email === newInvitee.email
          ? { ...invitee, status: "pending" } // Update status if email matches
          : invitee
      );

      // If no match was found in map, add the new invitee
      const emailExists = updatedInvitees.some(
        (inv) => inv.email === newInvitee.email
      );
      if (!emailExists) {
        updatedInvitees.push(newInvitee);
      }

      const newAsset = {
        ...updatedAsset,
        invitees: updatedInvitees,
      };

      // Update the allModelDevelopmentProjects state
      const updatedProjects = allModelDevelopmentProjects.map((project) =>
        project._id === updatedProjectId ? newAsset : project
      );

      setAllModelDevelopmentProjects(updatedProjects);
      showNotification("Invite sent successfully", "success");
    } catch (error) {
      console.error("Error sending invite:", error);
      showNotification(error?.message || "Failed to send invite", "danger");
    }
  };

  // Close project creation modal and reset state
  const handleCreateProjectModalClose = () => {
    setCreateProjectModal(false);
    setNewProjectDetails({
      name: "",
      description: "",
    });
  };

  // Get status icon based on project status
  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case "in design phase":
        return <Create style={{ color: "#435966" }} />;
      case "ready for publishing":
        return <CloudUpload style={{ color: "#435966" }} />;
      case "published":
        return <DoneAll style={{ color: "#435966" }} />;
      case "publishing failed":
        return <Error style={{ color: "#435966" }} />;
      default:
        return null;
    }
  };

  const _mapResourceToAppType = (resource) => {
    // Destructure the resource to exclude project_name and project_description
    const { title, description, ...rest } = resource;

    return {
      ...rest, // Spread all remaining properties
      name: title, // Add name property
      description: description, // Add description property
    };
  };

  // Show loading spinner while fetching projects
  if (loading.loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress
          color="quCreateColor"
          className={classes.loaderClass}
        />
        <Typography
          variant="h6"
          style={{ marginLeft: 16, textTransform: "none" }}
        >
          {loading.message}
        </Typography>
      </Box>
    );
  }

  return (
    <div>
      <CreateModelDevelopmentProjectModal
        newProjectDetails={newProjectDetails}
        setNewProjectDetails={setNewProjectDetails}
        createProjectModal={createProjectModal}
        handleCreateProjectModalClose={handleCreateProjectModalClose}
        handleCreateProject={handleCreateProject}
        projectCreationLoader={projectCreationLoader}
      />

      <AssetDashboard
        path={path}
        welcomeTitle="Model Development Design Dashboard"
        welcomeSubtitle="Start by creating a new model development project or reviewing your existing projects."
        allAssets={allModelDevelopmentProjects}
        setAllAssets={setAllModelDevelopmentProjects}
        initialAssets={initialModelDevelopmentProjects}
        noAssetMessage="Create or review your model development projects to get started."
        linkPath="/model/"
        getStatusIcon={getStatusIcon}
        CreateAssetCard={CreateCourseCard}
        setCreateAssetModal={setCreateProjectModal}
        createAssetButtonText="Create Project"
        handleSaveLabel={handleSaveLabel}
        handleInviteSend={handleInviteSend}
        asset_identifier="model_development"
        handleDeleteAsset={handleDeleteProject}
        handleCloneAsset={handleCloneProject}
        assetAdded={projectAdded}
        setAssetAdded={setProjectAdded}
        setInitialAssets={setInitialModelDevelopmentProjects}
        workflowConfig={{
          type: "model_dev",
          title: "Model Development Plan Workflow",
          description: `Create a structured plan for developing models, from defining the project and objectives to specifying technical details, governance, and compliance.`,
          steps: [
            {
              label: "1. Setup project & objectives",
              body: "Describe the project, its purpose, and objectives.",
            },
            {
              label: "2. Specify model card",
              body:
                "Document the model design, algorithms, data requirements, and training strategy.",
            },
            {
              label: "3. Validate policy & compliance regulations",
              body:
                "Outline internal governance requirements and external regulatory standards.",
            },
            {
              label: "4. Review the development document",
              body:
                "Review a consolidated overview of the plan and generate the formal " +
                "model development document.",
            },
          ],
          videoUrl:
            "https://qucoursify.s3.us-east-1.amazonaws.com/qcreate/demos/model-development-demo.mp4",
        }}
      />
    </div>
  );
}
